import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, merge } from 'rxjs';
import { map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { disconnect, setDomainMessageReceived, updateConnectionState } from './signalr.actions';

import { LoggerService } from '@app/services/logger.service';
import { HubConnectionState } from '@microsoft/signalr';
import { Action } from '@ngrx/store';
import { IDomainMessage, emptyAction, isDomainMessage, userLoginComplete } from '@ra-state';
import { ActionButtonStyles, NotificationType } from '@ra-web-tech-ui-toolkit/components';
import { HubConnectionError, HubStatus, SignalrService } from '@rockwell-automation-inc/service';
import { closeAllOpenDialogs, openWebTechDialog } from '../modal';

@Injectable({
  providedIn: 'root',
})
export class SignalREffects {
  private readonly actions$ = inject(Actions);
  private readonly signalRService = inject(SignalrService);

  logger = inject(LoggerService).withContext(SignalREffects);
  onLoadStartConnection$ = createEffect((): Observable<Action> => {
    // send an initial Connecting state to let the page load continue - otherwise when signalR is down, the page load blocks
    // test with notification service down and refresh dashboard to see the difference
    return this.actions$.pipe(
      ofType(userLoginComplete),
      switchMap(() => [updateConnectionState({ payload: HubConnectionState.Connecting })]),
    );
  });
  setStartConnection$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(userLoginComplete),
      tap(() => {
        this.signalRService.startConnection();
      }),
      switchMap(() => merge(this.signalRService.connectionStatus$, this.signalRService.onDomainMessage$)),
      mergeMap((msg: HubStatus | IDomainMessage) => {
        if (isDomainMessage(msg)) {
          return [setDomainMessageReceived({ payload: msg })];
        }
        if (msg instanceof HubConnectionError && msg.isNonRecoverable) {
          return [updateConnectionState({ payload: HubConnectionState.Disconnected, error: msg })];
        }
        if (msg instanceof HubConnectionError) {
          return [updateConnectionState({ payload: HubConnectionState.Reconnecting, error: msg })];
        }
        return [updateConnectionState({ payload: msg })];
      }),
    );
  });

  disconnectedModalConfig = {
    config: {
      title: 'Aw, snap! Disconnected',
      message: 'Disconnected from the server. Page will be refreshed to attempt reconnection.',
      buttons: [{ label: 'Ok', buttonStyle: ActionButtonStyles.Main }],
    },
    notificationType: NotificationType.Warning,
    mainBtnHandler: (): void => globalThis.location.reload(),
  };

  updateConnectionState$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(updateConnectionState),
      switchMap(({ payload, error }) => {
        this.logger.log('connection state', payload);
        const actions: Action[] = [];
        if (payload === HubConnectionState.Disconnected) {
          actions.push(openWebTechDialog(this.disconnectedModalConfig));
          this.logger.log(`signalr disconnected... error: ${error}`);
        }
        if (payload === HubConnectionState.Connected) {
          actions.push(closeAllOpenDialogs());
        }
        return actions;
      }),
    );
  });

  disconnect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(disconnect),
      tap(() => {
        this.logger.log('closing connection');
        this.signalRService.stopConnection();
      }),
      map(() => emptyAction()),
    );
  });
}
